import { useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { devError } from "~/lib/dev-log";

// Legal documents are large HTML strings. Import them dynamically so they are
// only fetched (as their own chunk) when the user actually opens the dialog,
// instead of being inlined into whichever component statically imports them.
const LEGAL_DOCS = {
  terms: () => import("~/data/legal-document/terms_7.html?raw"),
  privacy: () => import("~/data/legal-document/privacy.html?raw"),
  membership: () => import("~/data/legal-document/membership.html?raw"),
  shipping: () => import("~/data/legal-document/shipping.html?raw"),
} as const;

export type LegalDocKey = keyof typeof LEGAL_DOCS;

interface LegalDocDialogProps {
  doc: LegalDocKey;
  title: string;
  children: ReactNode;
}

export function LegalDocDialog({ doc, title, children }: LegalDocDialogProps) {
  const [html, setHtml] = useState<string | null>(null);

  const handleOpenChange = (open: boolean) => {
    if (!open || html !== null) return;
    LEGAL_DOCS[doc]()
      .then(module => setHtml(module.default))
      .catch(error => devError(`Failed to load legal document: ${doc}`, error));
  };

  return (
    <Dialog onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {html === null ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        ) : (
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </DialogContent>
    </Dialog>
  );
}
